/* tslint:disable:no-console */

import _cloneDeepWith from 'lodash/cloneDeepWith';
import _get from 'lodash/get';
import _keys from 'lodash/keys';
import _map from 'lodash/map';
import _isEmpty from 'lodash/isEmpty';
import _isString from 'lodash/isString';
import _merge from 'lodash/merge';
import _sortBy from 'lodash/sortBy';
import _set from 'lodash/set';
import _zipObject from 'lodash/zipObject';

import Vue from 'vue';
import camelcaseKeys from 'camelcase-keys';
import hash from 'object-hash';
import moment from 'moment-timezone';
import { parse as urlParse } from 'url';

import { Address, Maintenancejob, Photo } from './models';

export const appointmentdateformat = 'YYYY-MM-DD[T]HH:mm:ss.SSSZ';
export const appointmenttimeformat = 'hh:mm a';
export const appointmenttime24HRformat = 'HH:mm';

export function hashObj(obj: any): string {
  return hash(obj);
}

export function copyInto(destination: any, source: any, useCamelCase: boolean = true): void {
  if (useCamelCase) {
    return _merge(destination, toCamelCase(source));
  }
  return _merge(destination, source);
}

export function toCamelCase(source: any): any {
  return camelcaseKeys(source, {
    deep: true,
    exclude: [
      '_uuid',
      'set_1',
      'set_2',
    ],
  });
}

/**
 * Sort an object by it's keys
 */
export function sortObjByKeys(obj: any) {
  const keys = _sortBy(_keys(obj), ((key) => key));

  return _zipObject(keys, _map(keys, (key) => obj[key]));
}

/**
 * Omit properties by key, deeply.
 * Like lodash omit, but recursive.
 *
 * https://github.com/lodash/lodash/issues/723#issuecomment-194385740
 */
export function omitDeep(collection: any, excludeKeys: any) {

  function omitFn(value: any) {

    if (value && typeof value === 'object') {
      excludeKeys.forEach((key: string) => {
        delete value[key];
      });
    }
  }

  return _cloneDeepWith(collection, omitFn);
}
/**
 * Format in GBP
 */
export function formatCurrency(amount: string) {
  const num = parseFloat(amount);
  if (num === NaN) {
    throw Error('Number cannot be determined');
  }
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  }).format(num);
};

/**
 * Format date for display.
 *
 * @param date string|number|Date
 * @return string
 */
export function formatDateForDisplay(date?: string | number | Date) {
  const timezone = process.env.VUE_APP_TIMEZONE || 'Europe/London';

  return moment.utc(date).tz(timezone).format('DD/MM/YYYY');
}

/**
 * Format datetime for display.
 *
 * If datetime is not supplied then moment() uses "now".
 *
 * @param datetime string|number|Date
 * @return string
 */
export function formatDatetimeForDisplay(datetime?: string | number | Date) {
  const timezone = process.env.VUE_APP_TIMEZONE || 'Europe/London';

  return moment.utc(datetime).tz(timezone).format('DD/MM/YYYY HH:mm');
}

/**
 * Reformat datetime (e.g. from datetime-local form inputs) in UTC "YYYY-MM-DDTHH:mm"
 *
 * @param datetime string|number|Date
 * @return string
 */
export function datetimeToUTC(datetime?: string | number | Date) {
  const timezone = process.env.VUE_APP_TIMEZONE || 'Europe/London';

  return moment.tz(datetime, timezone).utc().format('YYYY-MM-DD[T]HH:mm');
}

/**
 * Reformat datetime (e.g. from datetime-local form inputs) in local "YYYY-MM-DDTHH:mm"
 *
 * @param datetime string|number|Date
 * @return string
 */
export function datetimeToLocalTime(datetime?: string | number | Date) {
  const timezone = process.env.VUE_APP_TIMEZONE || 'Europe/London';

  return moment.utc(datetime).tz(timezone).format('YYYY-MM-DD[T]HH:mm');
}

/**
 * Reformat date (e.g. from date form inputs) in UTC "YYYY-MM-DD"
 *
 * @param date string|number|Date
 * @return string
 */
export function dateToUTC(date?: string | number | Date) {
  const timezone = process.env.VUE_APP_TIMEZONE || 'Europe/London';

  return moment.tz(date, timezone).utc().format('YYYY-MM-DD');
}

/**
 * Reformat date (e.g. from date form inputs) in UTC "YYYY-MM-DD"
 *
 * @param date string|number|Date
 * @return string
 */
export function dateToLocalTime(date?: string | number | Date) {
  const timezone = process.env.VUE_APP_TIMEZONE || 'Europe/London';

  return moment.utc(date).tz(timezone).format('YYYY-MM-DD');
}

/**
 * Parse given string into a date object
 *
 * @param date string|number|Date
 * @return string
 */
export function parseDate(date?: string | number | Date) {
  const timezone = process.env.VUE_APP_TIMEZONE || 'Europe/London';

  return moment.utc(date).tz(timezone).toDate();
}

/**
 * Format Address as a string
 *
 * @param address Address
 * @param separator string
 * @return string
 */
export function formatAddress(address: Address, separator: string = '<br />') {
  const items = [
    address.line1,
    address.line2,
    address.town,
    address.county,
    address.postcode,
    // address.country, // Assuming all GB, for now
  ];
  return items
    .filter((el) => !_isEmpty((el || '').trim()))
    .join(separator);
}

/**
 * Remove an item from an array, using the item index
 *
 * @param items any[]
 * @param i number
 */
export function removeItemByIndex(items: any[], i: number) {
  return items.slice(0, i).concat(items.slice(i + 1, items.length));
}

/**
 * Swap 2 indexes in an array and return the resulting array
 *
 * @param array any[]
 * @param a number
 * @param b number
 * @return any[]
 */
export function swapArrayItems(array: any[], a: number, b: number): any[] {
  // console.log('swapArrayItems', 'array', array);
  // console.log('swapArrayItems', 'a', a);
  // console.log('swapArrayItems', 'b', b);
  let input = array;
  let temp = input[a];
  input[a] = input[b];
  input[b] = temp;
  return input;
}

/**
 * Swap 2 indexes in an array and return the resulting array
 *
 * @param array any[]
 * @param a number
 * @param b number
 * @return any[]
 */
export function confirmPrompt(message?: string): Promise<boolean> {
  if (!message) {
    message = 'Are you sure?';
  }
  return new Promise((resolve, reject) => {
    if (confirm(message)) {
      resolve(true);
    } else {
      reject(false);
    }
  });
}

/**
 * Display Axios/AWS Error
 *
 * @param error Axios Error response
 */
export function displayError(error: any): void {
  console.error(error);
  let message;
  switch (true) {
    case (_isString(error)):
      message = error;
      break;
    case (_get(error, 'response.data.message', '').length > 0):
      message = _get(error, 'response.data.message');
      break;
    case (_get(error, 'message', '').length > 0):
      message = _get(error, 'message');
      break;
    default:
      message = 'Changes not saved';
      break;
  }
  Vue.toasted.error(message);
}

/**
 * Return error message
 *
 * @param error Return Error response
 */
export function ReturndisplayError(error: any): void {
  console.error(error);
  let message;
  switch (true) {
    case (_isString(error)):
      message = error;
      break;
    case (_get(error, 'response.data.message', '').length > 0):
      message = _get(error, 'response.data.message');
      break;
    case (_get(error, 'message', '').length > 0):
      message = _get(error, 'message');
      break;
    default:
      message = 'Changes not saved';
      break;
  }
  return message;
}

/**
 * Full S3 URL of a given key, or the S3 origin if no key is supplied
 *
 * @param {String|undefined} key
 * @returns {String} Full URL to S3 Origin, with optional asset key
 */
export function s3Origin(key?: string): string {
  const origin = `https://${process.env.VUE_APP_S3_BUCKET}.s3.${process.env.VUE_APP_AWS_REGION}.amazonaws.com`;
  if (key === undefined) {
    return origin;
  }
  if (key.startsWith(origin)) {
    return key;
  }
  return `${origin}/${key}`;
}



/**
 * Request a photo using our AWS Serverless Image Handler.
 *
 * @see https://aws.amazon.com/solutions/serverless-image-handler/
 *
 * @param {Photo}   photo Photo, including `src` and optional `rotate` attribute
 * @param {object=} edits Edits, see Serverless Image Handler docs for more details
 *
 * @returns {string} Absolute URL to a Serverless Image Handler image
 */
export function photoSrc(photo: Photo, edits: object) {
  const { src, rotate } = photo;

  if (!src) {
    throw Error(`Photo has no src: ${JSON.stringify(photo)}`);
  }

  // Remove S3 Base Url prefix, if it exists
  if (photo.src.startsWith(s3Origin())) {
    photo.src = urlParse(photo.src).pathname!.slice(1);
  }

  // The Serverless Image Handler requires the Bucket and Key (without the S3 Bucket URL)
  const request = {
    bucket: process.env.VUE_APP_S3_BUCKET,
    key: photo.src,
    edits: {},
  };

  // Apply optional "edits" to the requested image (resize, etc)
  if (edits) {
    request.edits = edits;
  }

  // If the Photo has a rotate value, then set edits.rotate
  if (rotate) {
    _set(request, 'edits.rotate', rotate);
  }

  // Encode the stringified request params
  const base64EncodedRequest = Buffer.from(JSON.stringify(request)).toString('base64');

  // Prepend the Serverless Image Handler base URL
  return `${process.env.VUE_APP_RESIZE_ORIGIN}/${base64EncodedRequest}`;
};

/**
 * Photo Gallery URL.
 *
 * @param {Photo}  photo  Photo
 * @param {Maintenancejob} job Maintenancejob
 * @param {string} type   Type: "in"| or "out"
 *
 * @returns {string} Absolute URL to this Photo in the Photo Gallery.
 */
export function photoGalleryUrl(photo: Photo, job: Maintenancejob, type?: string) {
  const { src } = photo;
  const { id } = job;

  if (!src) {
    throw Error(`Photo has no src: ${JSON.stringify(photo)}`);
  }

  if (!id || id === 'new') {
    console.log('No job id', job);
    return '';
  }

  // Encode the stringified request params
  const base64EncodedSrc = Buffer.from(src).toString('base64');

  if (type) {
    return `/photos/${id}/${base64EncodedSrc}/${type}`;
  }

  return `/photos/${id}/${base64EncodedSrc}`
};

/**
 * Request a resized photo from our AWS Serverless Image Handler.
 *
 * This will resize the requested photo according to the pixel width set
 * in the PDF_IMG_WIDTH environment variable.
 *
 * @see https://aws.amazon.com/solutions/serverless-image-handler/
 *
 * @param {Photo} photo Photo, including src and optional rotate attribute
 *
 * @returns {string} Absolute URL to a Serverless Image Handler image
 */
export function resizedPhotoSrc(photo: Photo) {
  const edits = {
    resize: {
      width: parseInt(process.env.VUE_APP_RESIZE_IMG_WIDTH || '100'),
      fit: 'contain'
    },
  };

  return photoSrc(photo, edits);
}

/**
 * Request a resized logo from our AWS Serverless Image Handler.
 *
 * This will resize the requested photo according to the pixel width set
 * in the width parameter
 *
 * @see https://aws.amazon.com/solutions/serverless-image-handler/
 *
 * @param {Photo} photo Photo, including src and optional rotate attribute
 *
 * @returns {string} Absolute URL to a Serverless Image Handler image
 */
export function resizedLogoSrc(photo: Photo, width: number) {
  const edits = {
    resize: {
      width: width,
      fit: 'contain'
    },
  };

  return photoSrc(photo, edits);
}

/**
 * Request a resized image from our AWS Serverless Image Handler.
 *
 * This will resize the requested image according to the pixel width set
 * in the width parameter
 *
 * @see https://aws.amazon.com/solutions/serverless-image-handler/
 *
 * @param {Photo} image Photo, including src and optional rotate attribute
 *
 * @returns {string} Absolute URL to a Serverless Image Handler image
 */
export function resizedImageSrc(photo: Photo, width: number) {
  const edits = {
    resize: {
      width: width,
      fit: 'contain'
    },
  };

  return photoSrc(photo, edits);
}

/**
 * Copy a "Download Maintenancejob" button, ready to be pasted into
 * a HTML email.
 *
 * Exists with an  (if the requested Attachment type exists).
 *
 * @param {Maintenancejob} job         Maintenancejob
 * @param {String} attachmentType Attachment type (invoice|job)
 *
 * @return {Promise}
 */
export async function copyDownloadButton(job: Maintenancejob, attachmentType: string) {
  // This method only works for PDF Attachments (invoice|job)
  if (['invoice', 'job', 'summary'].includes(attachmentType) === false) {
    throw Error(`Unexpected Attachment.type: ${attachmentType}`);
  }

  const url: string = _get(job, `attachments.${attachmentType}`, '');

  if (url) {
    try {
      const encodedFullUrl = encodeURI(prependDownloadsURL(url));

      console.info('📋 URL:', encodedFullUrl);

      const buttonText = attachmentType == 'invoice' ? 'Download Invoice' : 'Download Job';

      // In Merlin this was 2 nested tables, but that was causing alignment issues for our new method
      const buttonHTML = `<table cellspacing="0" cellpadding="0" border="0" style="border: none;padding: 0;width: 200px;"><tr><td><a href="${encodedFullUrl}" target="_blank" style="display: block;text-align:center;background-color: #fd7e14;border-radius: 2px;font-family: Helvetica, Arial, sans-serif;font-size: 14px;color: #fae021;text-decoration: none;font-weight:bold;width: 200px;height: 40px;line-height: 20px;padding: 10px;">${buttonText}</a></td></tr></table>`;

      // Create temp DOM Element
      const tmpElement = document.body.appendChild(document.createElement('span'));
      tmpElement.insertAdjacentHTML('beforeend', buttonHTML);
      tmpElement.id = 'tmp-download-button';

      // console.info('📋 DOM Element:', tmpElement);

      // Select the DOM Element
      const range = document.createRange();
      range.selectNode(document.getElementById('tmp-download-button')!);
      window.getSelection()!.removeAllRanges();
      window.getSelection()!.addRange(range);

      // Copy & cleanup
      document.execCommand('copy');
      window.getSelection()!.removeAllRanges();
      tmpElement.remove();

      Vue.toasted.success(`"${buttonText}" button copied. Double-click to download.`);
    } catch (err) {
      console.error(err);

      Vue.toasted.error('Unable to copy to clipboard.');
    }
  } else {
    Vue.toasted.error('Double-click to generate the PDF first.');
  }
}

/**
 * Download a Maintenancejob Attachment from the Downloads S3 CloudFront Distribution.
 *
 * Downloads in a new window.
 *
 * @param {string} key Attachment S3 Key (or full URL if Maintenancejob is legacy)
 *
 * @return {Window|null}
 */
export function downloadAttachment(key: string): Window | null {
  const url = prependDownloadsURL(key);

  // We now only store the S3 Key, so construct the full S3 URL
  return window.open(url, '_blank');
}

/**
 * Prepend the full download URL to a file request (for an Attachemnt).
 *
 * @param {string} attachment Attachment location
 *
 * @return {string}
 */
export function prependDownloadsURL(attachment: string): string {
  // We originally stored the full S3 URL inc. protocol & domain into db
  if (attachment.startsWith('http://') || attachment.startsWith('https://')) {
    return attachment;
  }

  // We now only store the Attachment S3 Key, so construct the full S3 URL
  return `${process.env.VUE_APP_DOWNLOADS_URL}${attachment}`;
}

export function reverse(val: string) : string {
  return [...val].reverse().join("");
}

export function roundDate(datetime: string): string {
  const mom = moment(datetime);
  let remainder = 15 - (mom.minute() % 15);
  if (remainder === 15) remainder = 0;
  return moment(mom).add(remainder, "minutes").format(appointmentdateformat);
}
